import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {
  Checkbox,
  FormControlLabel,
  FormControl,
  MenuItem,
  Tooltip,
} from "@material-ui/core/";
import isEqual from "lodash.isequal";
import _ from "lodash";

import Dropdown from "../Dropdown";
import Spinner from "../Spinner";
import FilterSearchForm from "./search/searchForm";
import { Colors, BreakpointValues } from "../../constants";
import { useWindowSize } from "../../utils";
import { useDebounceEffect } from "../../utils/hooks/useDebounceEffect";

const propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  optionList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any,
      label: PropTypes.string,
    })
  ).isRequired,
  handleOutput: PropTypes.func.isRequired,
  value: PropTypes.any,
  optionMap: PropTypes.func,
  minChar: PropTypes.number,
};
const defaultProps = {
  optionMap: (option) => option,
  minChar: 0,
  value: null,
};

const FormControlField = styled(FormControl)`
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  display: -webkit-box;
`;

const ListItem = styled(MenuItem)`
  padding-top: 0;
  padding-bottom: 0;
  max-height: 300px;
  min-height: 48px;
`;

const ListItemLabel = styled(FormControlLabel)`
  padding-top: 4px;
  padding-bottom: 4px;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${Colors.GRAY};
  margin-bottom: 8px;
`;

const SpinnerContainer = styled.div`
  display: flex;
  padding: 16px;
  justify-content: center;
`;

export const checkboxDefaultInitialState = (optionList) =>
  optionList.reduce(
    (acc, option) => ({
      ...acc,
      [option.value]: false,
    }),
    {}
  );

const FilterCheckboxSearch = ({
  name,
  label,
  placeholder,
  value,
  loading,
  optionList = [],
  optionMap,
  searchTypeList = [],
  handleOutput,
  onSearch,
  submitOnChange,
  onChange,
  minChar,
  appliedFilters,
  tagBusinessUnityList = [],
  inputType = "text",
  isLowerCase = false,
  fieldNames,
}) => {
  const typeInitialState =
    searchTypeList && searchTypeList.length && searchTypeList[0].value;
  const [checkboxState, setCheckboxState] = useState(value);
  const [type, setType] = useState(typeInitialState);
  const [term, setTerm] = useState("");
  const [isSearch, setIsSearch] = useState(true);
  const currentWindowSize = useWindowSize();

  useEffect(() => {
    if (!isEqual(checkboxState, value)) {
      setCheckboxState(value);
      setType(typeInitialState);
      setTerm("");
    }
  }, []);

  useEffect(() => {
    if(appliedFilters[name] && Object.entries(appliedFilters[name]).length) {
      setCheckboxState(appliedFilters[name]);
      setType(typeInitialState);
      setTerm("");
    }
  }, [appliedFilters[name]])

  const handleChangeCheck = (event, checked, optValue) => {
    setCheckboxState({ ...checkboxState, [optValue]: checked });
  };

  const handleChangeTypeRadio = (event) => {
    setType(event.target.value);
    setTerm("");
  };

  const handleChangeTerm = (event) => {
    setTerm(event.target.value);
  };

  const toCamelCase = (str) => {
    const stringArray = str.split(' ');

    let result = '';
    stringArray.map(string => {
      const stringFormatted = string.toLowerCase().charAt(0).toUpperCase() + string.slice(1).toLowerCase();
      result = result + stringFormatted + ' ';
    })

    return result;
  }

  useDebounceEffect(
    () => {
      if (isSearch) {
        onSearch({
          type,
          term,
          skip: 0,
        });
      }
    },
    500,
    [type, term]
  );

  const handleSearchSubmit = () => {
    setIsSearch(true);
  };

  return (
    <Dropdown
      labelText={label}
      hasConfirmButtons={!!optionList.length}
      size={currentWindowSize.width <= BreakpointValues.md ? "280px" : "360px"}
      handleConfirm={(e) => {
        if (submitOnChange) {
          handleOutput(checkboxState);
        } else {
          onChange(checkboxState, e);
        }
      }}
      handleDismiss={() => {
        setCheckboxState(value);
      }}
      placement="bottom"
      appliedFilters={appliedFilters}
      fieldNames={fieldNames}
      name={name}
    >
      <FilterSearchForm
        typeList={searchTypeList}
        placeholder={placeholder}
        name={name}
        value={{
          type,
          term,
        }}
        type={inputType}
        handleOutput={handleSearchSubmit}
        onChangeType={handleChangeTypeRadio}
        onChangeTerm={handleChangeTerm}
        tagBusinessUnityList={tagBusinessUnityList}
        minChar={minChar}
      />
      {optionList.length || loading ? <Divider /> : null}
      {loading ? (
        <SpinnerContainer>
          <Spinner />
        </SpinnerContainer>
      ) : null}
      {optionList.length && !loading ? (
        <FormControlField
          component="fieldset"
          style={{ maxWidth: 360, overflowY: "scroll" }}
        >
          {optionList.map((option) => {
            if (optionMap(option).label.length > 31) {
              const label = isLowerCase ? 
                toCamelCase(optionMap(option).label).slice(0, 31).trim() +
                "..." :
                optionMap(option).label;

              return (
                <ListItem key={`${name}-${optionMap(option).value}`}>
                  <Tooltip title={optionMap(option).label} placement="top">
                    <ListItemLabel
                      control={
                        <Checkbox
                          checked={
                            checkboxState
                              ? checkboxState[optionMap(option).value]
                              : false
                          }
                          onChange={(event, checked) =>
                            handleChangeCheck(
                              event,
                              checked,
                              optionMap(option).value
                            )
                          }
                          name={name}
                          color="primary"
                        />
                      }
                      label={label}
                    />
                  </Tooltip>
                </ListItem>
              );
            } else {
              return (
                <ListItem key={`${name}-${optionMap(option).value}`}>
                  <ListItemLabel
                    control={
                      <Checkbox
                        checked={
                          checkboxState
                            ? checkboxState[optionMap(option).value]
                            : false
                        }
                        onChange={(event, checked) =>
                          handleChangeCheck(
                            event,
                            checked,
                            optionMap(option).value
                          )
                        }
                        name={name}
                        color="primary"
                      />
                    }
                    label={isLowerCase ? toCamelCase(optionMap(option).label) : optionMap(option).label}
                  />
                </ListItem>
              );
            }
          })}
        </FormControlField>
      ) : null}
    </Dropdown>
  );
};

FilterCheckboxSearch.propTypes = propTypes;
FilterCheckboxSearch.defaultProps = defaultProps;

export default FilterCheckboxSearch;
